"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function Hero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="223855" id="global-trade-integrated-solutions" className="relative w-full bg-secondary overflow-hidden py-24 md:py-32 lg:py-40 flex items-center justify-center">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-secondary to-secondary pointer-events-none opacity-60" />

      <div className="container px-4 md:px-6 relative z-10">
        <div ref={ref} className={`flex flex-col items-center text-center max-w-4xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h1 className="font-default text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold tracking-tighter text-white">
            Global Scale. Local Precision.
          </h1>
          
          <p className="mt-6 text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed border-l-4 border-primary pl-6 text-left">
            Through affiliated companies in the United States, Kenya, and Guinea, we deliver trade representation, procurement, WASH and humanitarian supply, industrial supply, project support, logistics, and transport services across East Africa and selected African markets.
          </p>
          
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto font-default font-semibold bg-primary text-secondary hover:bg-primary/90 shadow-lg">
              <Link to="/BusinessDivisionPage">
                Explore Our Divisions
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
            
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-default font-semibold border-white text-white hover:bg-white/10 backdrop-blur-sm">
              
            </Button>
          </div>
        </div>
      </div>
    </section>;
}